import { usePathname } from 'next/navigation'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { matchSceneByPathname } from '@/cinematics/utils/matchScene'
import { IScene } from '@/types'

const AUTH_ACTOR_VIEW_ID = 'AuthActor'

// The gate lives on the AuthActor script entry of the matched scene
// (IAuthScriptOptions.activation). The entry is found via the actors map
// (actorId → actor.viewId) with the entry's own `viewId` mirror as fallback —
// same actorId-matching rule Director uses.
export const isActivationEnabledFor = (
  scenes: Record<string, IScene> | undefined,
  pathname: string
): boolean => {
  const scene = matchSceneByPathname(scenes, pathname)
  if (!scene) return false

  // Only real, bound AuthActor ids — never `undefined` (IActor._id is optional,
  // and `includes(undefined)` used to match orphan/legacy script entries).
  const authActorIds = Object.values(scene.actors || {})
    .filter((actor) => actor.viewId === AUTH_ACTOR_VIEW_ID)
    .map((actor) => actor._id)
    .filter((id): id is string => id != null)

  // Evaluate EVERY entry Director would actually bind (strict `actorId` match,
  // matching Director.tsx). The `viewId` fallback is dropped: it let an unbound
  // entry hold the gate open on a whole route subtree and made deleting the
  // flag from the live entry (the documented kill switch) a no-op. `.some` so a
  // duplicate unflagged entry can't override a flagged one, and vice-versa —
  // removing the flag from all bound entries turns the gate off. See PRO-344 F1.
  return (scene.script || []).some(
    (entry) =>
      entry.actorId != null &&
      authActorIds.includes(entry.actorId) &&
      entry.options?.activation?.enabled === true
  )
}

/**
 * The single on/off gate for the partner-activation feature (PRO-13..15),
 * read from the **AuthActor script options** of the cinematic scene matched
 * to the current pathname (`options.activation.enabled` — IAuthScriptOptions;
 * scene resolved with Director's route-matching semantics via
 * `matchSceneByPathname`). Default is OFF: a route whose scene doesn't flag
 * its AuthActor never captures the URL param, never attaches it to an auth,
 * and can never show the failure modal — a crafted `?entitlementId=` outside
 * the flagged scenes is inert. Removing the key from the script entries is
 * the no-deploy kill switch (docs/ACTIVATION.md, "Enablement").
 *
 * Per-scene means EVERY route the id operates on needs the flag on its
 * scene's AuthActor script: capture (`/entel`) and every auth submit that
 * must link (recovery-bounce login on `/user/recover`/`/reset-password`,
 * catch-all auth scenes) — a scene left unflagged silently drops the linking
 * there.
 *
 * Reads the cinematics Zustand store, which Film initializes synchronously
 * before the first render — safe wherever AuthActor mounts. `readActivation-
 * Enabled` is the non-reactive twin for submit handlers.
 */
export const useActivationEnabled = (): boolean => {
  const scenes = useCinematicsStore((state) => state.scenes)
  const pathname = usePathname()
  return isActivationEnabledFor(scenes, pathname)
}

export const readActivationEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  return isActivationEnabledFor(
    useCinematicsStore.getState().scenes,
    window.location.pathname
  )
}
